import React, { useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { EducatorDto } from '../../../../api-service/clients/auto-generated-clients';
import { ChangeCourseEducatorsDialogComponent } from './change-course-educators-dialog-component';
import { CourseBulkEditResultDialogComponent } from './course-bulk-edit-result-dialog-component';
import { ILessonBulkEditResult, lessonResultSummary, skippedLessonItems, skippedLessonsText } from './bulk-edit-results';
import { useCompanyEducators } from './use-company-educators';

export interface IChangeLessonEducatorsDialogProps {
  lessonIds: string[];
  companyId: string;
  onCompleted?: () => void;
}

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function ChangeLessonEducatorsDialog({ lessonIds, companyId, onCompleted }: IChangeLessonEducatorsDialogProps) {
  const { educators, isLoadingEducators } = useCompanyEducators(companyId);
  const [isOpen, setIsOpen] = useState(true);
  const [educatorIdsToAdd, setEducatorIdsToAdd] = useState<string[]>([]);
  const [educatorIdsToRemove, setEducatorIdsToRemove] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<ILessonBulkEditResult>();

  function handleEducatorsToAddChange(chosen: EducatorDto[]) {
    setEducatorIdsToAdd(chosen.map(educator => educator.educatorId));
  }

  function handleEducatorsToRemoveChange(chosen: EducatorDto[]) {
    setEducatorIdsToRemove(chosen.map(educator => educator.educatorId));
  }

  async function handleConfirm() {
    setIsSaving(true);
    try {
      const response = await attendanceClient.addAndRemoveLessonEducators({ lessonIds, educatorIdsToAdd, educatorIdsToRemove });
      setResult(response);
    } catch {
      // The error message interceptor shows the reason, and the dialog stays open so the choice can be corrected
    } finally {
      setIsSaving(false);
    }
  }

  function handleClose() {
    setIsOpen(false);
  }

  function handleResultClose() {
    setIsOpen(false);
    onCompleted?.();
  }

  if (!isOpen) {
    return null;
  }

  if (result) {
    return (
      <CourseBulkEditResultDialogComponent
        title="Undervisere er rettet"
        summary={lessonResultSummary(result)}
        skippedItemsText={skippedLessonsText}
        skippedItems={skippedLessonItems(result)}
        onClose={handleResultClose}
      />
    );
  }

  return (
    <ChangeCourseEducatorsDialogComponent
      title={lessonIds.length === 1 ? 'Ret undervisere på den valgte lektion' : `Ret undervisere på de ${lessonIds.length} valgte lektioner`}
      description="Lektionerne beholder deres undervisere. De valgte undervisere tilføjes eller fjernes. Aflyste lektioner og lektioner med låst protokol ændres ikke."
      educators={educators}
      educatorIdsToAdd={educatorIdsToAdd}
      educatorIdsToRemove={educatorIdsToRemove}
      isLoadingEducators={isLoadingEducators}
      isSaving={isSaving}
      onEducatorsToAddChange={handleEducatorsToAddChange}
      onEducatorsToRemoveChange={handleEducatorsToRemoveChange}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
}
